/**
 * What the dashboard means by "a stream the backend pulls".
 *
 * RTMP is a **push** protocol — a drone or an encoder publishes into our RTMP
 * server and we read it back out. RTSP is a **pull** one — we open a session on
 * a fixed IP camera and it sends us frames. For everything above the ffmpeg
 * process those are the same thing: a URL the backend opens a reader on, and
 * the **scheme decides which**.
 *
 * The backend derives the reader the same way (`stream_url.reader_kind`), so
 * the two tiers cannot disagree about what a given URL is. Change the rule here
 * and you must change it there; `test_stream_url.py` and `streamTypes.test.ts`
 * are pinned against the same cases.
 */

/** A source the backend opens its own reader on, from an operator-supplied URL. */
export type PullType = 'rtmp' | 'rtsp';

const PULL_URL_RE = /^(rtmps?|rtsps?):\/\//i;
const RTSP_URL_RE = /^rtsps?:\/\//i;

/** Operator-facing copy for a rejected URL. One string, used everywhere. */
export const PULL_URL_HINT =
  'URL must start with rtmp://, rtmps://, rtsp:// or rtsps://';

/** Is this a URL the backend will accept as a stream source? */
export function isPullUrl(url: string): boolean {
  return PULL_URL_RE.test((url || '').trim());
}

/**
 * Which protocol a URL names. Defaults to `rtmp` for anything unrecognised so
 * a caller that has already validated the URL never gets `undefined` back —
 * the backend re-validates regardless, and rejects it there.
 */
export function pullTypeOf(url: string): PullType {
  return RTSP_URL_RE.test((url || '').trim()) ? 'rtsp' : 'rtmp';
}

/** Does this source type carry a URL the backend reads? */
export function isPullSource(type?: string): type is PullType {
  return type === 'rtmp' || type === 'rtsp';
}

/**
 * RTSP inherits the RTMP grant in `group_feed_access`; the Pi-guided aircraft
 * (`picam`) inherits the aircraft (`dji`) one.
 *
 * An operator allowed to add a pull stream may add either protocol. Without
 * this, introducing RTSP would silently hide RTSP feeds from every group that
 * has explicit feed rows until an admin ticked a new box — a new protocol
 * arriving as an invisible permission regression.
 */
export function feedAccessType(type: string): string {
  if (type === 'rtsp') return 'rtmp';
  // The Pi-guided aircraft is an aircraft feed: a group without the DJI grant does not
  // see it either (docs/PICAM_GUIDANCE.md). The server's PICAM_GROUP_ID check is the
  // real boundary; this keeps the list from offering what the server would refuse.
  if (type === 'picam') return 'dji';
  return type;
}

/**
 * The body for `/api/drone/video-source` (and `/stop`).
 *
 * `rtmp_url` is the wire field name for any pull URL — it predates RTSP and
 * older clients already send it, so it stays. The `source` field is derived
 * from the URL rather than passed through, which is why a stale client posting
 * `source: 'rtmp'` with an RTSP URL still lands on an RTSP reader.
 */
export function videoSourceBody(
  type: string,
  url: string,
  extra: Record<string, string | boolean> = {},
): Record<string, string | boolean> {
  return type === 'demo'
    ? { source: 'demo', ...extra }
    : { source: pullTypeOf(url), rtmp_url: url, ...extra };
}
